import { motion } from "framer-motion"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import useAppStore from "../../../store/useAppStore"
import useLayoutStore from "../../../store/useLayoutStore"
import LoadingSpinner from "../../elements/LoadingSpinner"
import UserStatsDayLogsTable from "./UserStatsDayLogsTable"
import UserStatsLineChart, { USER_STATS_NARROW_WIDTH_PX } from "./UserStatsLineChart"
import UserStatsMonthCalendar from "./UserStatsMonthCalendar"
import EmployeeMonthWorkSummary from "./EmployeeMonthWorkSummary"

const UserStatsView = () => {
    const { t } = useTranslation("stats")
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme)
    const userStatsDateFilter = useAppStore((state) => state.userStatsDateFilter)
    const userStatsMonthLogsByDay = useAppStore((state) => state.userStatsMonthLogsByDay)
    const loadingUserStatsMonthLogs = useAppStore((state) => state.loadingUserStatsMonthLogs)
    const ensureUserStatsMonthData = useAppStore((state) => state.ensureUserStatsMonthData)

    const { year, month } = userStatsDateFilter
    const monthKey = `${year}-${month}`
    const [selectedDay, setSelectedDay] = useState(null)
    const [isNarrowLayout, setIsNarrowLayout] = useState(false)
    const layoutRef = useRef(null)

    useEffect(() => {
        setSelectedDay(null)
    }, [monthKey])

    useLayoutEffect(() => {
        ensureUserStatsMonthData(month, year)
    }, [month, year, ensureUserStatsMonthData])

    useLayoutEffect(() => {
        const el = layoutRef.current
        if (!el) return undefined
        const sync = () => setIsNarrowLayout(el.getBoundingClientRect().width <= USER_STATS_NARROW_WIDTH_PX)
        sync()
        const observer = new ResizeObserver(sync)
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const handleSelectDay = useCallback((day) => {
        setSelectedDay(day)
    }, [])

    return (
        <div ref={layoutRef} className="usv-root">
            <EmployeeMonthWorkSummary
                logsByDay={userStatsMonthLogsByDay}
                year={year}
                monthIndex={month}
                isDarkTheme={isDarkTheme}
                loading={loadingUserStatsMonthLogs}
            />
            <motion.div
                key={`${monthKey}-top`}
                className="usv-content"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.19, ease: "easeOut" }}
            >
                <div className="usv-top-row">
                    <UserStatsMonthCalendar
                        isDarkTheme={isDarkTheme}
                        selectedDay={selectedDay}
                        onSelectDay={handleSelectDay}
                    />
                    <UserStatsDayLogsTable isDarkTheme={isDarkTheme} selectedDay={selectedDay} />
                </div>
            </motion.div>

            <div className="usv-chart-section">
                {loadingUserStatsMonthLogs ? (
                    <div className="usv-chart-loader">
                        <LoadingSpinner />
                    </div>
                ) : userStatsMonthLogsByDay ? (
                    <UserStatsLineChart
                        key={monthKey}
                        year={year}
                        monthIndex={month}
                        isDarkTheme={isDarkTheme}
                        logsByDay={userStatsMonthLogsByDay}
                        onSelectDay={handleSelectDay}
                        isNarrow={isNarrowLayout}
                    />
                ) : (
                    <p className={`usv-chart-empty ${isDarkTheme ? "dark-color" : ""}`}>
                        {t("chart.emptyMonth")}
                    </p>
                )}
            </div>
        </div>
    )
}

export default UserStatsView
